import React, { useState } from 'react';
import { Search, User, Route, MapPin, X, Plus } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Button,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge
} from '@city-sage/aurum-avis-labs-components';
import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { ItinerarySearchCard } from '../search/ItinerarySearchCard';
import { StopSearchCard } from '../search/StopSearchCard';
import { UserSearchCard } from '../search/UserSearchCard';
import { useDebounce } from '../../lib/hooks/useDebounce';
import CompactHeader from '../CompactHeader';

export function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('itineraries');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const { api } = useRepository();

  const ITEMS_PER_PAGE = 10;

  // Search itineraries with infinite query
  const {
    data: itinerariesData,
    isLoading: isLoadingItineraries,
    fetchNextPage: fetchNextItineraries,
    hasNextPage: hasNextItineraries,
    isFetchingNextPage: isFetchingNextItineraries
  } = useInfiniteQuery({
    queryKey: ['search', 'itineraries', debouncedQuery],
    queryFn: ({ pageParam = 1 }) => api.searchItineraries({
      q: debouncedQuery,
      limit: ITEMS_PER_PAGE,
      page: pageParam,
    }),
    enabled: debouncedQuery.length > 2,
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.results || lastPage.results.length < ITEMS_PER_PAGE) {
        return undefined;
      }
      return pages.length + 1;
    },
    initialPageParam: 1
  });

  // Search stops with infinite query
  const {
    data: stopsData,
    isLoading: isLoadingStops,
    fetchNextPage: fetchNextStops,
    hasNextPage: hasNextStops,
    isFetchingNextPage: isFetchingNextStops
  } = useInfiniteQuery({
    queryKey: ['search', 'stops', debouncedQuery],
    queryFn: ({ pageParam = 1 }) => api.searchStops({
      q: debouncedQuery,
      limit: ITEMS_PER_PAGE,
      page: pageParam,
    }),
    enabled: debouncedQuery.length > 2,
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.results || lastPage.results.length < ITEMS_PER_PAGE) {
        return undefined;
      }
      return pages.length + 1;
    },
    initialPageParam: 1
  });

  // Search accounts with infinite query
  const {
    data: accountsData,
    isLoading: isLoadingAccounts,
    fetchNextPage: fetchNextAccounts,
    hasNextPage: hasNextAccounts,
    isFetchingNextPage: isFetchingNextAccounts
  } = useInfiniteQuery({
    queryKey: ['search', 'accounts', debouncedQuery],
    queryFn: ({ pageParam = 1 }) => api.searchAccounts({
      q: debouncedQuery,
      limit: ITEMS_PER_PAGE,
      page: pageParam,
    }),
    enabled: debouncedQuery.length > 2,
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.results || lastPage.results.length < ITEMS_PER_PAGE) {
        return undefined;
      }
      return pages.length + 1;
    },
    initialPageParam: 1
  });

  const isLoading = isLoadingItineraries || isLoadingStops || isLoadingAccounts;

  // Flatten infinite query results
  const itineraries = itinerariesData?.pages.flatMap(page => page?.results || []) || [];
  const stops = stopsData?.pages.flatMap(page => page?.results || []) || [];
  const accounts = accountsData?.pages.flatMap(page => page?.results || []) || [];

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const getTotalResults = () => {
    return itineraries.length + stops.length + accounts.length;
  };

  return (
            <div className="flex min-h-dvh flex-col h-full bg-gray-50 pb-20 md:pb-0">
      <CompactHeader 
        icon={Search} 
        title="Search" 
        subtitle="Find itineraries, places, and users" 
      />
      
      {/* Search Section */}
      <div className="bg-white px-6 py-4 border-b border-gray-100">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search for itineraries, places, or users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            autoFocus
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Results Summary */}
        {debouncedQuery.length > 2 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            {isLoading ? (
              <span>Searching...</span>
            ) : (
              <span>
                {getTotalResults()} results for "{debouncedQuery}"
              </span>
            )}
          </div>
        )}
      </div>

      {/* Search Results */}
      <div className="flex-1 overflow-hidden mt-4">
        {debouncedQuery.length <= 2 ? (
          <div className="flex items-center justify-center h-full text-center p-8">
            <div className="space-y-3">
              <Search className="h-12 w-12 text-muted-foreground mx-auto" />
              <div>
                <h3 className="text-lg font-medium">Start searching</h3>
                <p className="text-muted-foreground">
                  Type at least 3 characters to search for itineraries, places, or users
                </p>
              </div>
            </div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <div className="bg-white border-b border-gray-100">
              <TabsList className="grid w-full grid-cols-3 h-8">
                <TabsTrigger value="itineraries" className="flex items-center gap-1 px-2 py-1 text-xs">
                  <Route className="h-3 w-3" />
                  <span>Itineraries</span>
                  {itineraries.length > 0 && (
                    <Badge variant="secondary" className="ml-0.5 text-xs px-1 py-0 h-4 min-w-[16px]">
                      {itineraries.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="stops" className="flex items-center gap-1 px-2 py-1 text-xs">
                  <MapPin className="h-3 w-3" />
                  <span>Places</span>
                  {stops.length > 0 && (
                    <Badge variant="secondary" className="ml-0.5 text-xs px-1 py-0 h-4 min-w-[16px]">
                      {stops.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-1 px-2 py-1 text-xs">
                  <User className="h-3 w-3" />
                  <span>Users</span>
                  {accounts.length > 0 && (
                    <Badge variant="secondary" className="ml-0.5 text-xs px-1 py-0 h-4 min-w-[16px]">
                      {accounts.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-hidden">
              <TabsContent value="itineraries" className="mt-0 h-full">
                <div className="h-full overflow-y-auto space-y-2">
                  {isLoadingItineraries && itineraries.length === 0 ? (
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="animate-pulse bg-white px-6 py-4">
                          <div className="flex gap-4">
                            <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : itineraries.length > 0 ? (
                    <>
                      {itineraries.filter(item => item.entity).map((itinerary) => (
                        <ItinerarySearchCard
                          key={itinerary.entity!.id}
                          itinerary={itinerary.entity!}
                          searchQuery={debouncedQuery}
                        />
                      ))}
                      
                      {/* Load More Button */}
                      {hasNextItineraries && (
                        <div className="bg-white px-6 py-4 border-t border-gray-100">
                          <Button
                            variant="outline"
                            onClick={() => fetchNextItineraries()}
                            disabled={isFetchingNextItineraries}
                            className="w-full justify-center"
                          >
                            <Plus className="h-4 w-4" />
                            {isFetchingNextItineraries ? 'Loading...' : 'Load More'}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-white px-6 py-12 text-center">
                      <Route className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No itineraries found</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="stops" className="mt-0 h-full">
                <div className="h-full overflow-y-auto space-y-2">
                  {isLoadingStops && stops.length === 0 ? (
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="animate-pulse bg-white px-6 py-4">
                          <div className="flex gap-4">
                            <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : stops.length > 0 ? (
                    <>
                      {stops.filter(item => item.entity).map((stop) => (
                        <StopSearchCard
                          key={stop.entity!.id}
                          stop={stop.entity!}
                          searchQuery={debouncedQuery}
                        />
                      ))}
                      
                      {/* Load More Button */}
                      {hasNextStops && (
                        <div className="bg-white px-6 py-4 border-t border-gray-100">
                          <Button
                            variant="outline"
                            onClick={() => fetchNextStops()}
                            disabled={isFetchingNextStops}
                            className="w-full justify-center"
                          >
                            <Plus className="h-4 w-4" />
                            {isFetchingNextStops ? 'Loading...' : 'Load More'}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-white px-6 py-12 text-center">
                      <MapPin className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No places found</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="users" className="mt-0 h-full">
                <div className="h-full overflow-y-auto space-y-2">
                  {isLoadingAccounts && accounts.length === 0 ? (
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="animate-pulse bg-white px-6 py-4">
                          <div className="flex gap-4">
                            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : accounts.length > 0 ? (
                    <>
                      {accounts.filter(item => item.entity).map((account) => (
                        <UserSearchCard
                          key={account.entity!.id}
                          account={account.entity!}
                          searchQuery={debouncedQuery}
                        />
                      ))}
                      
                      {/* Load More Button */}
                      {hasNextAccounts && (
                        <div className="bg-white px-6 py-4 border-t border-gray-100">
                          <Button
                            variant="outline"
                            onClick={() => fetchNextAccounts()}
                            disabled={isFetchingNextAccounts}
                            className="w-full justify-center"
                          >
                            <Plus className="h-4 w-4" />
                            {isFetchingNextAccounts ? 'Loading...' : 'Load More'}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-white px-6 py-12 text-center">
                      <User className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No users found</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        )}
      </div>
    </div>
  );
} 